import {
  BRANDED_PLAN_INELIGIBLE_HEALTH_RISKS,
  CLINICAL_INELIGIBLE_HEALTH_RISKS,
  HRT_INELIGIBLE_HEALTH_RISKS,
  MED_BMI_INCREASED_THRESHOLD,
  MED_BMI_SPECIAL_LOW_MIN,
  MED_BMI_THRESHOLD_BRANDED,
  MED_BMI_THRESHOLD_ORALS,
  MED_BMI_THRESHOLD_PROACTIVE_HEALTH,
  MED_BMI_THRESHOLD_SEMAGLUTIDE_LIRAGLUTIDE,
  ORALS_PLAN_INELIGIBLE_HEALTH_RISKS,
  SEMAGLUTIDE_LIRAGLUTIDE_PLAN_INELIGIBLE_HEALTH_RISKS,
} from "src/components/refactored-survey/question-sets/insurance-survey-questions/utils/insuranceConstants";
import { appConfig } from "src/config";
import {
  isAgeRangeStatusNoomClinicalEligible,
  isEatingDisorderStatusNoomClinicalEligible,
  isFlowEligibleForClinicalProducts,
  isFlowEligibleForHRTProducts,
  isInAppMedUpgrade,
  isLongevityMicrodoseEnabled,
  isNoomWeightMedBranching,
  isPregnantStatusHealthCheckEligible,
  isQualifiedForPersonalizedDosing,
  isZepboundFeatureEnabled,
} from "@utils/userSegment/features";
import { getSubdivision } from "./meristemContext";
import zipState from "zip-state";
import { calculateBMIImperial, calculateBMIMetric } from "src/primitive/bmi";
import {
  healthRiskOptions,
  oralsHealthRiskOptions,
} from "src/pages/survey/currentHealthRiskMedical";
import { getSurveyAnswers } from "src/hooks/survey/answers";
import { hrtHealthRiskOptions } from "src/pages/hrt-survey/currentHealthRiskHRT";
import {
  isB2BUpsellClinicalRoute,
  isCalorieClinicalRoute,
  isFreemiumHealthRoute,
  isHealthRoute,
  isHRTRoute,
} from "@utils/userSegment";
import getStore from "./redux/store";

export enum MED_SKU {
  BRANDED_ZEPBOUND_VIAL = "BRANDED_ZEPBOUND_VIAL",
  BRANDED = "BRANDED",
  COMPOUNDED = "COMPOUNDED",
  GENERIC_LIRAGLUTIDE = "GENERIC_LIRAGLUTIDE",
  ORALS_METFORMIN = "ORALS_METFORMIN",
  HRT_CREAM = "HRT_CREAM",
  HRT_PATCH = "HRT_PATCH",
  COMPOUNDED_MICRODOSE = "COMPOUNDED_MICRODOSE",
  COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER = "COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER",
}

export enum NON_MED_PLAN {
  NOOM_WEIGHT = "NOOM_WEIGHT",
  FREEMIUM = "FREEMIUM",
}

export type HealthPlan = MED_SKU | NON_MED_PLAN;

export enum MED_FLOW {
  HRT = "hrt",
  CLINICAL = "clinical",
  HEALTH = "health",
}

export const NO_US_ZIPCODE = "noUsZipcode";

type UserInfo = {
  bmi: number;
  highestBMI: number;
  previousMedications: string[];
  whichMedication: string[];
  medicalHealthRisks: HealthRiskOption[];
  hrtHealthRisks: HealthRiskOption[];
  state: string;
};

export const ELIGIBLE_SKUS_FOR_SWITCH = [
  MED_SKU.COMPOUNDED,
  MED_SKU.ORALS_METFORMIN,
];

type HealthRiskOption =
  | (typeof healthRiskOptions)[number]
  | (typeof oralsHealthRiskOptions)[number]
  | (typeof hrtHealthRiskOptions)[number];

export type NamedEligibilityCheck =
  | "isQualifiedForPD"
  | "isNotEligibleForCompoundedSemaglutide";

type SkuSpecificAddressableEligibilityChecks = {
  [K in NamedEligibilityCheck]?: () => boolean;
};

type BmiCriteria = {
  threshold?: number;
  increasedThreshold?: number;
  increasedThresholdStates?: string[];
};

type EligibilityInfo = {
  flow: MED_FLOW;
  bmiCriteria?: BmiCriteria;
  bmiRules?: Array<(context: BmiContext) => boolean>;
  ineligibleHealthRisks: HealthRiskOption[];
  ineligibleStates?: string[];
  flowEligibilityCheck?: () => boolean;
  skuSpecificAddressableEligibilityChecks?: SkuSpecificAddressableEligibilityChecks;
};

type EligibilityCheckOptions = {
  checkBMI: boolean;
  checkHealthRisks: boolean;
  checkState: boolean;
  checkFlow: boolean;
  disabledNamedChecks?: NamedEligibilityCheck[];
  requirePreviousMedicationsAnswer?: boolean;
  requireHighestBMIAnswer?: boolean;
};

type BmiContext = {
  currentBMI: number;
  highestBMI: number;
  previousMedications?: string[];
  whichMedication?: string[];
  state: string;
  effectiveThreshold: number;
  lowMin: number;
  requirePreviousMedicationsAnswer?: boolean;
  requireHighestBMIAnswer?: boolean;
};

export function computeEffectiveThreshold(
  criteria: BmiCriteria | undefined,
  state: string,
  checkState = true
) {
  const { threshold, increasedThreshold, increasedThresholdStates } = criteria;
  return increasedThreshold &&
    checkState &&
    increasedThresholdStates?.includes(state)
    ? increasedThreshold
    : threshold;
}

const atOrAboveEffectiveThreshold = ({
  currentBMI,
  effectiveThreshold,
}: BmiContext) => currentBMI >= effectiveThreshold;

const isInLowBmiWindow = (
  currentBMI: number,
  lowMin: number,
  threshold: number
) => currentBMI >= lowMin && currentBMI < threshold;

const didTakeWeightLossMeds = (
  previousMedications?: string[],
  whichMedication?: string[]
) => {
  const tookMeds =
    !!previousMedications &&
    (previousMedications[0] === "yes" ||
      previousMedications[0] === "takingWeightLossMed");

  const tookGLP1Medication =
    !!whichMedication &&
    whichMedication.some((med) =>
      [
        "wegovy",
        "ozempic",
        "compoundedSemaglutide",
        "zepbound",
        "mounjaro",
        "compoundedTirzepatide",
      ].includes(med)
    );
  return tookMeds && tookGLP1Medication;
};

const lowBmiWithPriorGLP1Meds = ({
  currentBMI,
  highestBMI,
  previousMedications,
  whichMedication,
  effectiveThreshold,
  lowMin,
  requirePreviousMedicationsAnswer,
  requireHighestBMIAnswer,
}: BmiContext) => {
  if (!currentBMI) return false;
  if (!isInLowBmiWindow(currentBMI, lowMin, effectiveThreshold)) return false;
  if (requireHighestBMIAnswer) {
    if (!highestBMI || highestBMI < effectiveThreshold) return false;
  }
  if (!requirePreviousMedicationsAnswer) {
    // For health route: block full-dose GLP-1 if BMI < threshold
    // For other routes: allow (return true)
    if (isHealthRoute() || isFreemiumHealthRoute()) {
      return false; // Block low BMI on health route
    }
    return true;
  }
  return didTakeWeightLossMeds(previousMedications, whichMedication);
};

const compoundedEligibilityCriteria = {
  bmiCriteria: {
    threshold: MED_BMI_THRESHOLD_SEMAGLUTIDE_LIRAGLUTIDE,
  },
  bmiRules: [atOrAboveEffectiveThreshold, lowBmiWithPriorGLP1Meds],
  ineligibleHealthRisks: [
    ...SEMAGLUTIDE_LIRAGLUTIDE_PLAN_INELIGIBLE_HEALTH_RISKS,
    ...CLINICAL_INELIGIBLE_HEALTH_RISKS,
  ],
  ineligibleStates: appConfig.COMPOUNDED_PLAN_INELIGIBLE_US_STATES,

  flowEligibilityCheck: () => isFlowEligibleForClinicalProducts(),
  flow: MED_FLOW.CLINICAL,
  skuSpecificAddressableEligibilityChecks: {
    isQualifiedForPD: () => isQualifiedForPersonalizedDosing(),
  },
};

const ELIGIBILITY_INFO: Record<MED_SKU, EligibilityInfo> = {
  [MED_SKU.BRANDED_ZEPBOUND_VIAL]: {
    bmiCriteria: {
      threshold: MED_BMI_THRESHOLD_BRANDED,
      increasedThreshold: MED_BMI_INCREASED_THRESHOLD,
      increasedThresholdStates: appConfig.GLP1_INCREASED_BMI_US_STATES,
    },
    bmiRules: [atOrAboveEffectiveThreshold],
    ineligibleHealthRisks: [
      ...BRANDED_PLAN_INELIGIBLE_HEALTH_RISKS,
      ...CLINICAL_INELIGIBLE_HEALTH_RISKS,
    ],
    ineligibleStates: appConfig.BRANDED_PLAN_INELIGIBLE_US_STATES,
    flowEligibilityCheck: () =>
      isFlowEligibleForClinicalProducts() && isZepboundFeatureEnabled(),

    flow: MED_FLOW.CLINICAL,
  },
  [MED_SKU.BRANDED]: {
    bmiCriteria: {
      threshold: MED_BMI_THRESHOLD_BRANDED,
      increasedThreshold: MED_BMI_INCREASED_THRESHOLD,
      increasedThresholdStates: appConfig.GLP1_INCREASED_BMI_US_STATES,
    },
    bmiRules: [atOrAboveEffectiveThreshold],
    ineligibleHealthRisks: [
      ...BRANDED_PLAN_INELIGIBLE_HEALTH_RISKS,
      ...CLINICAL_INELIGIBLE_HEALTH_RISKS,
    ],
    ineligibleStates: appConfig.BRANDED_PLAN_INELIGIBLE_US_STATES,

    flowEligibilityCheck: () => isFlowEligibleForClinicalProducts(),
    flow: MED_FLOW.CLINICAL,
  },
  [MED_SKU.COMPOUNDED]: compoundedEligibilityCriteria,
  [MED_SKU.COMPOUNDED_MICRODOSE]: {
    ...compoundedEligibilityCriteria,
    ineligibleStates: appConfig.COMPOUNDED_MICRODOSE_PLAN_INELIGIBLE_US_STATES,
    flowEligibilityCheck: () => isFlowEligibleForClinicalProducts(),
  },
  [MED_SKU.GENERIC_LIRAGLUTIDE]: {
    bmiCriteria: {
      threshold: MED_BMI_THRESHOLD_SEMAGLUTIDE_LIRAGLUTIDE,
    },
    bmiRules: [atOrAboveEffectiveThreshold, lowBmiWithPriorGLP1Meds],
    ineligibleHealthRisks: [
      ...SEMAGLUTIDE_LIRAGLUTIDE_PLAN_INELIGIBLE_HEALTH_RISKS,
      ...CLINICAL_INELIGIBLE_HEALTH_RISKS,
    ],
    ineligibleStates: appConfig.LIRAGLUTIDE_PLAN_INELIGIBLE_US_STATES,

    flowEligibilityCheck: () => isFlowEligibleForClinicalProducts(),
    flow: MED_FLOW.CLINICAL,
    skuSpecificAddressableEligibilityChecks: {
      isNotEligibleForCompoundedSemaglutide: () => {
        const eligibleProducts = getEligibleMedProductsBase({
          checkBMI: true,
          checkHealthRisks: true,
          checkState: true,
          checkFlow: true,
          requirePreviousMedicationsAnswer: true,
          requireHighestBMIAnswer: true,
          disabledNamedChecks: ["isNotEligibleForCompoundedSemaglutide"],
        });
        return !eligibleProducts.includes(MED_SKU.COMPOUNDED);
      },
    },
  },
  [MED_SKU.ORALS_METFORMIN]: {
    bmiCriteria: { threshold: MED_BMI_THRESHOLD_ORALS },
    bmiRules: [atOrAboveEffectiveThreshold],
    ineligibleHealthRisks: [
      ...ORALS_PLAN_INELIGIBLE_HEALTH_RISKS,
      ...CLINICAL_INELIGIBLE_HEALTH_RISKS,
    ],
    ineligibleStates: appConfig.ORALS_PLAN_INELIGIBLE_US_STATES,
    flowEligibilityCheck: () => isFlowEligibleForClinicalProducts(),
    flow: MED_FLOW.CLINICAL,
  },
  [MED_SKU.HRT_CREAM]: {
    ineligibleHealthRisks: HRT_INELIGIBLE_HEALTH_RISKS,
    flowEligibilityCheck: () => isFlowEligibleForHRTProducts(),
    ineligibleStates: appConfig.HRT_INELIGIBLE_US_STATES,
    flow: MED_FLOW.HRT,
  },
  [MED_SKU.HRT_PATCH]: {
    ineligibleHealthRisks: HRT_INELIGIBLE_HEALTH_RISKS,
    flowEligibilityCheck: () => isFlowEligibleForHRTProducts(),
    ineligibleStates: appConfig.HRT_INELIGIBLE_US_STATES,
    flow: MED_FLOW.HRT,
  },
  [MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER]: {
    bmiCriteria: {
      threshold: MED_BMI_THRESHOLD_PROACTIVE_HEALTH,
    },
    bmiRules: [atOrAboveEffectiveThreshold],
    ineligibleHealthRisks: [
      ...SEMAGLUTIDE_LIRAGLUTIDE_PLAN_INELIGIBLE_HEALTH_RISKS,
      ...CLINICAL_INELIGIBLE_HEALTH_RISKS,
    ],
    ineligibleStates: appConfig.LONGEVITY_MICRODOSE_PLAN_INELIGIBLE_US_STATES,
    flowEligibilityCheck: () =>
      isFlowEligibleForClinicalProducts() &&
      isLongevityMicrodoseEnabled() &&
      !(
        isInAppMedUpgrade() ||
        isNoomWeightMedBranching() ||
        isCalorieClinicalRoute()
      ),
    flow: MED_FLOW.CLINICAL,
  },
};

function getBMIFromSurvey({
  heightFeet,
  heightInch,
  heightCm,
  weightField,
  weightKgField,
}: {
  heightFeet?: number;
  heightInch?: number;
  heightCm?: number;
  weightField?: number;
  weightKgField?: number;
}) {
  if (
    heightFeet !== undefined &&
    heightInch !== undefined &&
    weightField !== undefined
  ) {
    return calculateBMIImperial(heightFeet, heightInch, weightField);
  }
  if (heightCm !== undefined && weightKgField !== undefined) {
    return calculateBMIMetric(heightCm, weightKgField);
  }
  return NaN;
}

export function getBMI() {
  const { heightFeet, heightInch, weight, heightCm, weightKg } =
    getSurveyAnswers();

  return getBMIFromSurvey({
    heightFeet,
    heightInch,
    heightCm,
    weightField: weight,
    weightKgField: weightKg,
  });
}

function getHighestBMI() {
  const { heightFeet, heightInch, highestWeight, heightCm, highestWeightKg } =
    getSurveyAnswers();

  return getBMIFromSurvey({
    heightFeet,
    heightInch,
    heightCm,
    weightField: highestWeight,
    weightKgField: highestWeightKg,
  });
}

export function passesMinBMIForMedSwitchingBuyflow() {
  const bmi = getBMI();

  return bmi >= MED_BMI_THRESHOLD_SEMAGLUTIDE_LIRAGLUTIDE;
}

function getMedState() {
  const { zipcode } = getSurveyAnswers();
  if (zipcode === NO_US_ZIPCODE) {
    return zipcode;
  }
  // Note (Alex): Intially, we use the IP state as the source of truth for the state eligibility;
  // Once we collect the zipcode, we will only trust the state determined based on the zipcode
  let state = getSubdivision();
  if (zipcode !== undefined) {
    state = zipState(zipcode);
  }
  return state;
}

export function hasIneligibleHealthRisks(
  healthRisks: HealthRiskOption[],
  ineligibleHealthRisks: HealthRiskOption[]
) {
  const ineligibleAnswers = healthRisks.filter((answer) =>
    ineligibleHealthRisks.includes(answer)
  );

  return !!ineligibleAnswers.length;
}

export function getMedicalHealthRisks(): HealthRiskOption[] {
  const { currentHealthRiskMedical } = getSurveyAnswers();

  return (currentHealthRiskMedical || []).filter(
    (el) => typeof el === "string"
  ) as HealthRiskOption[];
}

export function getHrtHealthRisks(): HealthRiskOption[] {
  const { currentHealthRiskHRT } = getSurveyAnswers();

  return currentHealthRiskHRT || [];
}

export function isSpecialLowBMIIneligibleCohort({
  currentBMI,
  highestBMI,
  previousMedications,
  whichMedication,
  bmiThreshold,
}: {
  currentBMI: number;
  highestBMI: number;
  previousMedications: string[];
  whichMedication: string[] | undefined;
  bmiThreshold: number;
}): boolean {
  return (
    !didTakeWeightLossMeds(previousMedications, whichMedication) &&
    currentBMI >= MED_BMI_SPECIAL_LOW_MIN &&
    currentBMI < bmiThreshold &&
    highestBMI >= bmiThreshold
  );
}

export function getMedBMIThreshold(considerUserLocation = false, currentBMI) {
  let eligibilityKeys: Array<keyof typeof ELIGIBILITY_INFO> = [];
  // Get the products that pass the extra check, this means we don't
  // take into account the orals bmi threshold if it's disabled
  eligibilityKeys = eligibilityKeys.concat(
    getEligibleMedProductsBase({
      checkBMI: false,
      checkHealthRisks: false,
      checkState: false,
      checkFlow: true,
      requirePreviousMedicationsAnswer: false,
      requireHighestBMIAnswer: false,

      disabledNamedChecks: [
        "isQualifiedForPD",
        "isNotEligibleForCompoundedSemaglutide",
      ],
    })
  );

  const userState = getMedState();
  const bmiThresholds = eligibilityKeys
    .map((key) => {
      const { bmiCriteria } = ELIGIBILITY_INFO[key];
      if (!bmiCriteria?.threshold) return undefined;
      return computeEffectiveThreshold(
        bmiCriteria,
        userState,
        considerUserLocation
      );
    })
    .filter((v): v is number => typeof v === "number");

  if (currentBMI <= MED_BMI_SPECIAL_LOW_MIN) {
    bmiThresholds.push(MED_BMI_SPECIAL_LOW_MIN);
  }

  return Math.min(...bmiThresholds);
}

function meetsEligibilityInfo(
  sku: MED_SKU,
  eligibilityInfo: EligibilityInfo,
  userInfo: UserInfo,
  eligibilityCheckOptions: EligibilityCheckOptions
) {
  const {
    bmiCriteria,
    ineligibleHealthRisks,
    ineligibleStates,
    flowEligibilityCheck,
    skuSpecificAddressableEligibilityChecks,
    flow,
  } = eligibilityInfo;
  const { medicalHealthRisks, hrtHealthRisks, state } = userInfo;
  const healthRisks =
    flow === MED_FLOW.HRT ? hrtHealthRisks : medicalHealthRisks;
  const {
    checkBMI,
    checkHealthRisks,
    checkState,
    disabledNamedChecks,
    checkFlow,
  } = eligibilityCheckOptions;

  let passesBmiCheck = true;
  if (checkBMI && eligibilityInfo.bmiRules?.length && bmiCriteria?.threshold) {
    const effectiveThreshold = computeEffectiveThreshold(
      bmiCriteria,
      state,
      checkState
    );

    const context: BmiContext = {
      currentBMI: userInfo.bmi,
      highestBMI: userInfo.highestBMI,
      previousMedications: userInfo.previousMedications,
      whichMedication: userInfo.whichMedication,
      state,
      effectiveThreshold: effectiveThreshold as number,
      lowMin: MED_BMI_SPECIAL_LOW_MIN,
      requirePreviousMedicationsAnswer:
        eligibilityCheckOptions.requirePreviousMedicationsAnswer,
      requireHighestBMIAnswer: eligibilityCheckOptions.requireHighestBMIAnswer,
    };

    passesBmiCheck = eligibilityInfo.bmiRules.some((rule) => rule(context));
  }

  const passesHealthRiskCheck =
    checkHealthRisks && ineligibleHealthRisks
      ? !hasIneligibleHealthRisks(ineligibleHealthRisks, healthRisks)
      : true;
  let passesStateCheck = true;
  if (checkState) {
    if (state === NO_US_ZIPCODE) {
      passesStateCheck = false;
    } else if (ineligibleStates) {
      passesStateCheck = !ineligibleStates.includes(state);
    }
  }
  const passesExtraCheck =
    checkFlow && flowEligibilityCheck ? flowEligibilityCheck() : true;

  let passesSkuSpecificChecks = true;
  if (skuSpecificAddressableEligibilityChecks) {
    const allCheckNames = Object.keys(
      skuSpecificAddressableEligibilityChecks
    ) as NamedEligibilityCheck[];
    const checksToRun = disabledNamedChecks
      ? allCheckNames.filter((check) => !disabledNamedChecks.includes(check))
      : allCheckNames;
    passesSkuSpecificChecks = checksToRun.every((checkName) => {
      const checkFn = skuSpecificAddressableEligibilityChecks[checkName];
      return checkFn ? checkFn() : true;
    });
  }

  let passesEligibleSaleItemsCheck = true;
  if (isB2BUpsellClinicalRoute()) {
    const { eligibleSaleItems } = getStore().getState().recommendationContext;
    passesEligibleSaleItemsCheck = eligibleSaleItems?.some(
      (saleItem) => saleItem.medSku === sku
    );
  }

  return (
    passesBmiCheck &&
    passesHealthRiskCheck &&
    passesStateCheck &&
    passesExtraCheck &&
    passesSkuSpecificChecks &&
    passesEligibleSaleItemsCheck
  );
}

export function getEligibleMedProductsBase(
  options: EligibilityCheckOptions & {
    stateOverwrite?: string;
  } = {
    checkBMI: true,
    checkHealthRisks: true,
    checkState: true,
    requirePreviousMedicationsAnswer: true,
    requireHighestBMIAnswer: true,
    checkFlow: true,
  },
  flow?: MED_FLOW
): Array<MED_SKU> {
  const eligibleProducts = [];
  const bmi = getBMI();
  const highestBMI = getHighestBMI();
  const medicalHealthRisks = getMedicalHealthRisks();
  const hrtHealthRisks = getHrtHealthRisks();
  const state = options.stateOverwrite ?? getMedState();
  const { previousMedications, whichMedication } = getSurveyAnswers();

  const userInfo = {
    bmi,
    highestBMI,
    previousMedications,
    whichMedication,
    medicalHealthRisks,
    hrtHealthRisks,
    state,
  };

  let eligibleSkus = Object.values(MED_SKU);
  if (flow && options.checkFlow) {
    eligibleSkus = eligibleSkus.filter(
      (sku) => ELIGIBILITY_INFO[sku].flow === flow
    );
  }
  eligibleSkus.forEach((sku) => {
    if (meetsEligibilityInfo(sku, ELIGIBILITY_INFO[sku], userInfo, options)) {
      eligibleProducts.push(sku);
    }
  });

  return eligibleProducts;
}

/**
 * Wrapper over getEligibleMedProductsBase that adds additional eligibility checks
 * required for health check (age, pregnant, eating disorder).
 */
export function getEligibleMedProductsForHealthCheck(
  options?: Parameters<typeof getEligibleMedProductsBase>[0]
): MED_SKU[] {
  if (
    !isAgeRangeStatusNoomClinicalEligible() ||
    !isPregnantStatusHealthCheckEligible() ||
    !isEatingDisorderStatusNoomClinicalEligible()
  ) {
    return [];
  }

  return getEligibleMedProductsBase(
    {
      checkBMI: true,
      checkHealthRisks: true,
      checkState: true,
      checkFlow: true,
      requirePreviousMedicationsAnswer: false,
      requireHighestBMIAnswer: false,
      ...options,
    },
    MED_FLOW.CLINICAL
  );
}

export function getEligibleMedProducts(
  options: EligibilityCheckOptions & {
    stateOverwrite?: string;
  } = {
    checkBMI: true,
    checkHealthRisks: true,
    checkState: true,
    checkFlow: true,
    requirePreviousMedicationsAnswer:
      !isHealthRoute() && !isFreemiumHealthRoute(),
    requireHighestBMIAnswer: !isHealthRoute() && !isFreemiumHealthRoute(),
  },
  flow?: MED_FLOW
): Array<MED_SKU> {
  if (isHealthRoute()) {
    return getEligibleMedProductsForHealthCheck(options);
  }

  const eligibleProducts = getEligibleMedProductsBase(options, flow);

  // For freemium upsell health route we only want to show the proactive health plan
  if (isFreemiumHealthRoute()) {
    return eligibleProducts.filter(
      (sku) => sku === MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER
    );
  }

  return eligibleProducts;
}

/**
 * Checks if the eligible products include full-dose GLP-1 medications.
 * Full-dose GLP-1 includes: COMPOUNDED, GENERIC_LIRAGLUTIDE, and COMPOUNDED_MICRODOSE.
 *
 * @param eligibleProducts - Array of eligible MED_SKU products
 * @returns true if eligibleProducts includes COMPOUNDED, GENERIC_LIRAGLUTIDE, or COMPOUNDED_MICRODOSE
 */
export function isFulldoseGlp1Eligible(
  eligibleProducts: Array<MED_SKU>
): boolean {
  return (
    eligibleProducts.includes(MED_SKU.COMPOUNDED) ||
    eligibleProducts.includes(MED_SKU.GENERIC_LIRAGLUTIDE) ||
    eligibleProducts.includes(MED_SKU.COMPOUNDED_MICRODOSE)
  );
}

export const getMedFlowByRoute = () => {
  if (isHRTRoute()) {
    return MED_FLOW.HRT;
  }
  if (isHealthRoute()) {
    return MED_FLOW.HEALTH;
  }
  return MED_FLOW.CLINICAL;
};

/**
 * Returns the eligible med tiers based on the recommended med tier.
 * The order is used to determine the visibility of the med tiers in the plan selection page.
 *
 * @param recommendedMedTier - The recommended med tier.
 * @returns The eligible med tiers.
 */
export function getEligibleMedProductsPlanSelection(
  recommendedMedTier: MED_SKU,
  eligibleMedProducts: Array<MED_SKU>
): Array<MED_SKU> {
  let medTiers: Array<MED_SKU> = [];
  switch (recommendedMedTier) {
    case MED_SKU.ORALS_METFORMIN:
      medTiers = [
        MED_SKU.ORALS_METFORMIN,
        MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER,
        MED_SKU.COMPOUNDED,
        MED_SKU.GENERIC_LIRAGLUTIDE,
        MED_SKU.COMPOUNDED_MICRODOSE,
        MED_SKU.BRANDED,
        MED_SKU.BRANDED_ZEPBOUND_VIAL,
      ];
      break;
    case MED_SKU.BRANDED:
      medTiers = [
        MED_SKU.BRANDED,
        MED_SKU.BRANDED_ZEPBOUND_VIAL,
        MED_SKU.COMPOUNDED,
        MED_SKU.GENERIC_LIRAGLUTIDE,
        MED_SKU.COMPOUNDED_MICRODOSE,
        MED_SKU.ORALS_METFORMIN,
        MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER,
      ];
      break;
    case MED_SKU.BRANDED_ZEPBOUND_VIAL:
      medTiers = [
        MED_SKU.BRANDED_ZEPBOUND_VIAL,
        MED_SKU.BRANDED,
        MED_SKU.COMPOUNDED,
        MED_SKU.GENERIC_LIRAGLUTIDE,
        MED_SKU.COMPOUNDED_MICRODOSE,
        MED_SKU.ORALS_METFORMIN,
        MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER,
      ];
      break;
    case MED_SKU.COMPOUNDED_MICRODOSE:
      medTiers = [
        MED_SKU.COMPOUNDED_MICRODOSE,
        MED_SKU.COMPOUNDED,
        MED_SKU.GENERIC_LIRAGLUTIDE,
        MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER,
        MED_SKU.BRANDED,
        MED_SKU.BRANDED_ZEPBOUND_VIAL,
        MED_SKU.ORALS_METFORMIN,
      ];
      break;
    case MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER:
      medTiers = [
        MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER,
        MED_SKU.COMPOUNDED_MICRODOSE,
        MED_SKU.COMPOUNDED,
        MED_SKU.GENERIC_LIRAGLUTIDE,
        MED_SKU.BRANDED,
        MED_SKU.ORALS_METFORMIN,
        MED_SKU.BRANDED_ZEPBOUND_VIAL,
      ];
      break;
    default:
      medTiers = [
        MED_SKU.COMPOUNDED,
        MED_SKU.GENERIC_LIRAGLUTIDE,
        MED_SKU.COMPOUNDED_MICRODOSE,
        MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER,
        MED_SKU.BRANDED,
        MED_SKU.BRANDED_ZEPBOUND_VIAL,
        MED_SKU.ORALS_METFORMIN,
      ];
      break;
  }
  return medTiers.filter((medTier) => eligibleMedProducts.includes(medTier));
}

export const MED_DISCLOSURE_LINKS = {
  [MED_FLOW.HRT]:
    "https://www.noom.com/static/Safety-Information-for-HRT-Medications.pdf",
  [MED_FLOW.CLINICAL]:
    "https://www.noom.com/static/Safety-Information-for-Certain-Weight-Loss-Medications.pdf",
};

type SKU_INFO = {
  isGlp1: boolean;
};

export const MED_SKU_INFO: Record<MED_SKU, SKU_INFO> = {
  BRANDED: {
    isGlp1: true,
  },
  BRANDED_ZEPBOUND_VIAL: {
    isGlp1: true,
  },
  COMPOUNDED: {
    isGlp1: true,
  },
  COMPOUNDED_MICRODOSE: {
    isGlp1: true,
  },
  COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER: {
    isGlp1: true,
  },
  GENERIC_LIRAGLUTIDE: {
    isGlp1: true,
  },
  HRT_CREAM: {
    isGlp1: false,
  },
  HRT_PATCH: {
    isGlp1: false,
  },
  ORALS_METFORMIN: {
    isGlp1: false,
  },
};

export function getEligibleGlp1Products() {
  return getEligibleMedProducts().filter(
    (eligibleMedProduct) => MED_SKU_INFO[eligibleMedProduct].isGlp1
  );
}
